<template>
  <b-card
    aria-label="Formulaire de modification de facture fournisseur"
    role="form"
  >
    <h4 class="mb-3">
      Modifier la Facture Fournisseur #{{ invoiceId }}
    </h4>
    <p class="text-muted mb-4">
      Modifiez les paramètres de la facture. Le système recalculera automatiquement le montant.
    </p>

    <div
      v-if="isLoading === true"
      class="text-center mb-2"
    >
      <b-spinner
        variant="primary"
        label="Chargement..."
      />
    </div>

    <!-- Paid Invoice Warning -->
    <b-alert
      v-if="!isLoading && invoice && invoice.status === 'paid'"
      show
      variant="danger"
      class="mb-3"
    >
      <div class="d-flex align-items-center">
        <feather-icon
          icon="LockIcon"
          class="mr-2"
        />
        <div>
          <strong>Facture payée</strong><br>
          Cette facture est marquée comme payée et ne peut pas être modifiée.
          <b-button
            variant="outline-light"
            size="sm"
            class="ml-2"
            @click="$router.push({ name: 'invoice-list-direx' })"
          >
            Retour à la liste
          </b-button>
        </div>
      </div>
    </b-alert>

    <b-form
      v-if="!isLoading && invoice && invoice.status !== 'paid'"
      @submit.prevent="updateInvoice"
    >
      <!-- Display current period (read-only) -->
      <b-row>
        <b-col cols="12">
          <b-alert
            show
            variant="info"
            class="mb-3"
          >
            <div class="d-flex align-items-center">
              <feather-icon
                icon="CalendarIcon"
                class="mr-2"
              />
              <div>
                <strong>Période actuelle:</strong> {{ formatDate(invoice.start_date) }} - {{ formatDate(invoice.end_date) }}
                <br>
                <small class="text-muted">Les dates ne peuvent pas être modifiées. Créez une nouvelle facture si nécessaire.</small>
              </div>
            </div>
          </b-alert>
        </b-col>
      </b-row>

      <!-- Pricing Configuration -->
      <b-row>
        <b-col md="6">
          <b-form-group
            label="Prix par commande sous-traitance (TND)"
            label-for="outsourcingPrice"
            aria-label="Prix unitaire pour les commandes sous-traitées"
            description="Prix appliqué aux commandes livrées par des sociétés de sous-traitance."
          >
            <b-form-input
              id="outsourcingPrice"
              v-model="outsourcingPrice"
              type="number"
              step="0.001"
              placeholder="0.150"
            />
          </b-form-group>
        </b-col>

        <b-col md="6">
          <b-form-group
            label="Prix par commande directe (TND)"
            label-for="companyPrice"
            aria-label="Prix unitaire pour les commandes directes"
            description="Prix appliqué aux commandes livrées directement par l'entreprise."
          >
            <b-form-input
              id="companyPrice"
              v-model="companyPrice"
              type="number"
              step="0.001"
              placeholder="0.200"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Status Display (Read-only) -->
      <b-row>
        <b-col cols="12">
          <b-alert
            show
            variant="light"
            class="mb-3"
          >
            <div class="d-flex align-items-center justify-content-between">
              <div>
                <strong>Statut actuel:</strong>
                <b-badge
                  :variant="invoice.status === 'paid' ? 'success' : 'danger'"
                  class="ml-2"
                >
                  {{ invoice.status === 'paid' ? 'Payée' : 'Non payée' }}
                </b-badge>
              </div>
              <small class="text-muted">
                Pour changer le statut, utilisez le bouton dans la liste des factures
              </small>
            </div>
          </b-alert>
        </b-col>
      </b-row>

      <!-- Current Totals Display -->
      <b-row v-if="invoice.pricing_breakdown">
        <b-col cols="12">
          <b-card
            bg-variant="light"
            class="mb-3"
          >
            <h6 class="mb-3">
              Totaux actuels
            </h6>
            <b-row>
              <b-col md="6">
                <div class="mb-2">
                  <strong>Commandes sous-traitance:</strong> {{ invoice.pricing_breakdown.outsourcing_orders }}
                </div>
                <div class="mb-2">
                  <strong>Total sous-traitance:</strong> {{ formatCurrency(invoice.pricing_breakdown.outsourcing_total) }}
                </div>
              </b-col>
              <b-col md="6">
                <div class="mb-2">
                  <strong>Commandes directes:</strong> {{ invoice.pricing_breakdown.company_orders }}
                </div>
                <div class="mb-2">
                  <strong>Total direct:</strong> {{ formatCurrency(invoice.pricing_breakdown.company_total) }}
                </div>
              </b-col>
            </b-row>
            <hr>
            <div class="text-right">
              <h5>
                <strong>Total à payer:</strong> {{ formatCurrency(invoice.total_amount) }}
              </h5>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Action Buttons -->
      <b-row>
        <b-col cols="12">
          <b-button
            type="submit"
            variant="success"
            class="mr-2"
          >
            <feather-icon
              icon="SaveIcon"
              class="mr-1"
            />
            Enregistrer les modifications
          </b-button>

          <b-button
            variant="outline-secondary"
            @click="$router.push({ name: 'invoice-list-direx' })"
          >
            <feather-icon
              icon="XIcon"
              class="mr-1"
            />
            Annuler
          </b-button>
        </b-col>
      </b-row>

      <b-alert
        v-model="alertVisible"
        :show="alertVisible"
        :variant="alertVariant"
        dismissible
        class="mt-3"
      >
        {{ alertMessage }}
      </b-alert>
    </b-form>
  </b-card>
</template>

<script>
import {
  BCard, BForm, BFormGroup, BFormInput, BButton, BAlert, BRow, BCol, BSpinner,
} from 'bootstrap-vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'EditInvoiceProvider',
  components: {
    BCard,
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    BAlert,
    BRow,
    BCol,
    BSpinner,
  },
  data() {
    return {
      invoiceId: this.$route.params.id,
      invoice: null,
      outsourcingPrice: 0.150,
      companyPrice: 0.200,
      status: 'unpaid',
      isLoading: false,
      alertVisible: false,
      alertVariant: '',
      alertMessage: '',
      statusOptions: [
        { value: 'unpaid', text: 'Non payée' },
        { value: 'paid', text: 'Payée' },
      ],
    }
  },
  mounted() {
    this.loadInvoice()
  },
  methods: {
    async loadInvoice() {
      this.isLoading = true
      try {
        const response = await axios.get(`/api/invoices/invoice-providers/${this.invoiceId}/`)
        this.invoice = response.data

        // Set form values from loaded invoice
        this.outsourcingPrice = parseFloat(this.invoice.outsourcing_price_per_order) || 0.150
        this.companyPrice = parseFloat(this.invoice.company_price_per_order) || 0.200
        this.status = this.invoice.status || 'unpaid'
      } catch (error) {
        console.error('Error loading invoice:', error)
        this.showAlert('danger', 'Erreur lors du chargement de la facture')
        this.showToast('danger', 'Erreur lors du chargement de la facture')
      } finally {
        this.isLoading = false
      }
    },
    async updateInvoice() {
      // Prevent update of paid invoices
      if (this.invoice.status === 'paid') {
        this.showToast('warning', 'Impossible de modifier une facture payée')
        return
      }

      this.isLoading = true
      try {
        // Recalculate with new pricing
        const response = await axios.post(
          `/api/invoices/invoice-providers/${this.invoiceId}/recalculate_with_pricing/`,
          {
            outsourcing_price_per_order: parseFloat(this.outsourcingPrice),
            company_price_per_order: parseFloat(this.companyPrice),
          },
        )

        this.invoice = response.data.invoice
        this.showToast('success', 'Facture mise à jour avec succès!')

        // Redirect to list after a short delay
        setTimeout(() => {
          this.$router.push({ name: 'invoice-list-direx' })
        }, 1500)
      } catch (error) {
        console.error('Error updating invoice:', error)
        const errorMsg = error.response?.data?.error || 'Erreur lors de la mise à jour de la facture.'
        this.showAlert('danger', errorMsg)
        this.showToast('danger', errorMsg)
      } finally {
        this.isLoading = false
      }
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      })
    },
    formatCurrency(amount) {
      if (!amount) return '0.000 TND'
      return `${parseFloat(amount).toLocaleString('fr-FR', {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      })} TND`
    },
    showAlert(variant, message) {
      this.alertVariant = variant
      this.alertMessage = message
      this.alertVisible = true
    },
    showToast(variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: variant === 'success' ? 'Succès' : 'Erreur',
          icon: variant === 'success' ? 'CheckCircleIcon' : 'AlertCircleIcon',
          text,
          variant,
        },
      })
    },
  },
}
</script>

<style scoped>
.b-form-group {
  margin-bottom: 1.5rem;
}

.alert {
  border-radius: 0.5rem;
}

select.form-control {
  cursor: pointer;
}
</style>
